import BookingSummary from '@components/BookingSummary';
import ClinicDropdown from '@components/ClinicDropdown';
import DatePicker from '@components/DatePicker';
import { TimePicker } from '@components/TimePicker';
import PractitionerTimePickerList from '@components/TimePicker/PractitionerTimePickerList';
import { TimezoneInfo } from '@components/TimezoneInfo';
import useAnalytics from '@hooks/useAnalytics';
import { useIsChooseDoctorFlow } from '@hooks/useIsChooseDoctorFlow';
import {
  fetchClinics,
  fetchPractitioner,
  fetchTimeSlots,
  LegacyClinic,
} from '@packages/cortex-sdk';
import {
  getDateInTimezone,
  norwayTimeZone,
  parseDay,
  useBannerStore,
  useClinicStore,
  useDateStore,
  useNavigationStore,
  usePersonalDataStore,
  useServiceTreeStore,
  useWeekRanges,
} from '@packages/shared';
import { Actions } from '@packages/types';
import { StepWrapper } from '@packages/ui-web';
import { useQuery } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { shallow } from 'zustand/shallow';

export default function SelectDateAndTime() {
  const setEventProps = useAnalytics();

  const { t } = useTranslation('booking');
  const { selectedDate, setSelectedDate } = useDateStore()((state) => ({
    selectedDate: state.selectedDate,
    setSelectedDate: state.setSelectedDate,
  }));

  const isMobile = useMediaQuery({ maxWidth: '768px' });
  const daysAhead = isMobile ? 5 : 7;
  const [startDate, setStartDate] = useState(getDateInTimezone());
  const [endDate, setEndDate] = useState(
    getDateInTimezone(addDays(parseDay(startDate), daysAhead - 1)),
  );

  const {
    next: nextWeek,
    prev: prevWeek,
    weeks,
    currentWeekIndex,
    jumpToDate,
    canGoBack,
  } = useWeekRanges({
    daysAhead,
    selectedDay: selectedDate,
    endDate: parseDay(endDate),
    startDate: parseDay(startDate),
    setSelectedDate: (day) => setSelectedDate(day),
    setStartDate: (day) => setStartDate(getDateInTimezone(day)),
    setEndDate: (day) => setEndDate(getDateInTimezone(day)),
  });

  const { queries, advanceStep, setQueries } = useNavigationStore(
    (state) => ({
      setQueries: state.setQueries,
      queries: state.queries,
      advanceStep: state.advanceStep,
    }),
    shallow,
  );
  const setPersonalDetails = usePersonalDataStore()(
    (state) => state.setPersonalDetails,
  );
  const { clinic, setClinic } = useClinicStore()((state) => ({
    clinic: state.clinic,
    setClinic: state.setClinic,
  }));

  const { getServiceByShortId } = useServiceTreeStore(
    (state) => ({
      getServiceByShortId: state.getServiceByShortId,
    }),
    shallow,
  );

  const service = queries.service
    ? getServiceByShortId(queries.service)
    : undefined;
  const serviceUuid = service?.id ?? '';

  const [selectedClinic, setSelectedClinic] = useState<LegacyClinic | null>(
    null,
  );

  const isChooseGpPractitionerService = useIsChooseDoctorFlow();

  const openBanner = useBannerStore((state) => state.openBanner);

  useQuery(
    ['clinics', serviceUuid, queries.city, queries.clinicId],
    ({ signal }) =>
      fetchClinics({
        serviceId: serviceUuid,
        signal,
      }),
    {
      enabled: !!serviceUuid && !!queries.city,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        data
          .filter((clinic) => clinic.address.city === queries.city)
          .filter((clinic) => {
            if (
              clinic.shortId === queries.clinicId ||
              (queries.clinicId &&
                clinic.legacyShortIds?.includes(queries.clinicId))
            ) {
              setClinic(clinic);
            }
          });
      },
    },
  );

  const activeClinic =
    (isChooseGpPractitionerService ? selectedClinic : clinic) ?? null;

  const { data: timeslots, isLoading: isLoadingPerClinic } = useQuery(
    ['timeSlots', serviceUuid, activeClinic?.id, startDate, endDate],
    ({ signal }) =>
      fetchTimeSlots({
        serviceId: serviceUuid,
        clinicId: activeClinic!.id,
        fromDate: `${startDate}T00:00:00.000Z`,
        toDate: `${endDate}T23:59:59.000Z`,
        practitionerId: queries.practitionerId,
        signal,
      }),
    {
      enabled: !!serviceUuid && !!activeClinic?.id,
      refetchOnWindowFocus: false,
    },
  );

  const handleOnClick = (time?: string, practitionerId?: string) => {
    const resolvedPractitionerId = isChooseGpPractitionerService
      ? queries.practitionerId
      : practitionerId;

    if (resolvedPractitionerId) {
      setQueries({ practitionerId: resolvedPractitionerId });
    }

    setPersonalDetails({
      date: selectedDate,
      time,
      practitionerId,
    });

    setEventProps({
      event: {
        action: Actions.SelectTime,
        vertical: queries.vertical,
        speciality: queries.speciality,
        service: queries.service,
        clinic: activeClinic?.shortId,
        time,
        practitionerId: resolvedPractitionerId,
      },
    });

    advanceStep();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      openBanner();
    }, 20 * 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const price = service?.price;

  const bookByPractitioner = service?.requiresBookWithPractitioner;

  const { data: practitioner } = useQuery(
    ['practitioner', queries.practitionerId],
    ({ signal }) =>
      fetchPractitioner({ practitionerId: queries.practitionerId!, signal }),
    { enabled: isChooseGpPractitionerService && !!queries.practitionerId },
  );

  const { data: gpClinics } = useQuery(
    ['gp-clinics', serviceUuid, queries.practitionerId],
    ({ signal }) => fetchClinics({ serviceId: serviceUuid, signal }),
    {
      enabled: isChooseGpPractitionerService && !!serviceUuid && !!practitioner,
      select: (data) => {
        const ids = new Set(practitioner!.clinics.map((c) => c.id));
        return data
          .filter((c) => ids.has(c.id))
          .sort((a, b) => a.name.localeCompare(b.name));
      },
    },
  );

  /*
   * If there are no available time slots for the first day, set selected day
   * to the first day with time slots available
   */
  useEffect(() => {
    if (timeslots) {
      const firstAvailableTimeSlot = timeslots.find((ts) => ts.isAvailable);
      setSelectedDate(
        firstAvailableTimeSlot?.startTime.split('T')[0] || selectedDate,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeslots]);

  if (!activeClinic && !isChooseGpPractitionerService) {
    return null;
  }

  const handleSelectClinic = (clinic: LegacyClinic) => {
    setSelectedClinic(clinic);
    setClinic(clinic);
    setQueries({ clinicId: clinic.shortId });
    setEventProps({
      event: {
        action: Actions.ChooseDoctorSelectDifferentClinic,
        clinic: clinic.shortId,
        practitionerId: queries.practitionerId,
      },
    });
  };

  return (
    <StepWrapper title={t('steps.select_datetime.title')} stepWidth="max-w-2xl">
      <div className="w-full md:max-w-[512px]">
        <BookingSummary
          hasClinic={!!activeClinic}
          hasTimeStamp={false}
          practitionerId={queries.practitionerId}
          currentStep="SelectDateAndTime"
        />

        <TimezoneInfo clinicTimezone={norwayTimeZone} />
        {isChooseGpPractitionerService && queries.practitionerId && (
          <div className="mb-10 flex flex-col gap-2">
            <p>{t('steps.select_datetime.select_clinic')}</p>
            <ClinicDropdown
              clinics={gpClinics ?? []}
              serviceId={serviceUuid}
              practitionerId={queries.practitionerId}
              handleSelectClinic={handleSelectClinic}
              selectedClinic={activeClinic}
            />
          </div>
        )}

        <DatePicker
          selectedDate={selectedDate}
          timeSlots={timeslots || []}
          setSelectedDate={setSelectedDate}
          weeks={weeks}
          currentWeekIndex={currentWeekIndex}
          next={nextWeek}
          prev={prevWeek}
          isLoading={
            isChooseGpPractitionerService && !activeClinic
              ? true
              : isLoadingPerClinic
          }
          canGoBack={canGoBack}
        />
      </div>

      <div className="mt-4 flex w-full flex-col gap-8">
        <div className="w-full">
          {bookByPractitioner &&
          !queries.practitionerId &&
          activeClinic &&
          !isChooseGpPractitionerService ? (
            <div className="w-full">
              <PractitionerTimePickerList
                clinicId={activeClinic.id}
                timeSlots={timeslots || []}
                selectedDate={selectedDate}
                isLoading={isLoadingPerClinic}
                serviceId={serviceUuid!}
                daysAhead={daysAhead}
                setSelectedDate={jumpToDate}
                onClick={(time, practitionerId) => {
                  handleOnClick(time, practitionerId);
                }}
                price={price}
              />
            </div>
          ) : (
            <div className="m-auto w-full max-w-[512px]">
              <TimePicker
                timeSlots={timeslots || []}
                selectedDate={selectedDate}
                isLoading={
                  isChooseGpPractitionerService && !activeClinic
                    ? true
                    : isLoadingPerClinic
                }
                daysAhead={daysAhead}
                onClick={(time, practitionerId) => {
                  handleOnClick(time, practitionerId);
                }}
                practitionerId={queries.practitionerId}
                price={price}
              />
            </div>
          )}
        </div>
      </div>
    </StepWrapper>
  );
}
